// Done left-side rail. Mirror of BacklogRail but anchored to the left edge
// and fed from the `done` flag instead of `backlogged`. Compact list of
// completed projects grouped by the priority they carried when finished,
// each with a "Reopen" action that clears the done flag.

import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';

import {
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_ORDER,
} from '../../pm/constants';
import { updateProject } from '../../pm/firebase';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import { Project, ProjectDepartment, ProjectPriority, Task } from '../../pm/types';
import { IconChevDown, IconClose, IconFilter } from './Icons';
import { usePmCanvasLeft } from './usePmCanvasLeft';

interface Filters {
  department: 'all' | ProjectDepartment;
  priority: 'all' | ProjectPriority;
}

interface Props {
  open: boolean;
  onClose: () => void;
  projects: Project[];   // all projects; we filter to done inside
  tasks: Task[];         // for counts on each row
  /** Notifies the parent that a rail row is being dragged so Grid lanes can
   *  light up as drop targets (dropping a done card on a lane reopens it). */
  onProjectDragStart?: (id: string) => void;
  onProjectDragEnd?: () => void;
}

const DoneRail: React.FC<Props> = ({
  open,
  onClose,
  projects,
  tasks,
  onProjectDragStart,
  onProjectDragEnd,
}) => {
  const history = useHistory();
  const canvasLeft = usePmCanvasLeft();
  const { departments, getDept } = useProjectDepartments();
  const [filters, setFilters] = useState<Filters>({ department: 'all', priority: 'all' });
  const [menuOpen, setMenuOpen] = useState<'department' | 'priority' | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Click-away dismisses the filter menu. Re-installs each time menuOpen
  // flips so we don't trap a stale closure.
  useMemo(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const done = useMemo(() => projects.filter((p) => p.done), [projects]);
  const filtered = useMemo(() => {
    return done.filter((p) => {
      if (filters.department !== 'all' && p.department !== filters.department) return false;
      if (filters.priority !== 'all' && p.priority !== filters.priority) return false;
      return true;
    });
  }, [done, filters]);

  const taskCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of tasks) {
      m[t.projectId] = (m[t.projectId] || 0) + 1;
    }
    return m;
  }, [tasks]);

  // Group by the priority the project carried when it was completed.
  const groups = useMemo(() => {
    return PROJECT_PRIORITY_ORDER
      .map((id) => ({
        id,
        label: PROJECT_PRIORITY_LABELS[id],
        color: PROJECT_PRIORITY_COLORS[id],
        items: filtered.filter((p) => p.priority === id),
      }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const unprioritizedItems = filtered.filter((p) => !p.priority);
  const activeFilterCount =
    (filters.department !== 'all' ? 1 : 0) + (filters.priority !== 'all' ? 1 : 0);

  function deptLabel(): string {
    if (filters.department === 'all') return 'All departments';
    return getDept(filters.department as ProjectDepartment)?.label ?? 'Department';
  }
  function priLabel(): string {
    if (filters.priority === 'all') return 'All priorities';
    return PROJECT_PRIORITY_LABELS[filters.priority as ProjectPriority];
  }

  async function reopen(projectId: string) {
    try {
      await updateProject(projectId, { done: null });
    } catch {
      // Failures surface through the realtime subscription; the row stays
      // put and the user can retry.
    }
  }

  // Portal to body + .pm-app wrapper for the same reasons as BacklogRail:
  // escape IonRouterOutlet's stacking context and reinstate the --pm-*/--rr-*
  // token scope.
  //
  // The rail lives inside a fixed, overflow:hidden clip box that spans the
  // content area (canvas left edge → viewport right). The clip does two
  // things: it masks the rail so the closed/animating panel never paints over
  // the navs, and — because the rail slides within the clip from its own left
  // edge — the open animation reads as emerging from the canvas's left edge
  // (where the peek tab sits) instead of travelling across the whole screen.
  return createPortal(
    <div className="pm-app pm-bl-portal">
    <div className="pm-done-rail-clip" style={{ left: canvasLeft }}>
    <aside
      className={`pm-bl-rail pm-bl-rail-left${open ? ' is-open' : ''}`}
      aria-hidden={!open}
    >
      <div className="pm-bl-rail-hd">
        <div>
          <div className="pm-bl-rail-eyebrow">Done</div>
          <div className="pm-bl-rail-count">
            {done.length} project{done.length === 1 ? '' : 's'} complete
          </div>
        </div>
        <button
          type="button"
          className="pm-bl-rail-close"
          onClick={onClose}
          aria-label="Close done"
        >
          <IconClose size={16} />
        </button>
      </div>

      <div className="pm-bl-rail-filters" ref={menuRef}>
        <div className="pm-bl-rail-filter-wrap">
          <button
            type="button"
            className={`pm-bl-rail-filter-btn${filters.department !== 'all' ? ' is-on' : ''}`}
            onClick={() => setMenuOpen((v) => (v === 'department' ? null : 'department'))}
          >
            <IconFilter size={11} />
            <span>{deptLabel()}</span>
            <IconChevDown size={11} />
          </button>
          {menuOpen === 'department' ? (
            <div className="pm-bl-rail-menu">
              <FilterItem
                checked={filters.department === 'all'}
                onClick={() => { setFilters((f) => ({ ...f, department: 'all' })); setMenuOpen(null); }}
              >
                <span>All departments</span>
                <span className="ct">{done.length}</span>
              </FilterItem>
              <div className="pm-bl-rail-menu-sep" />
              {departments.map((d) => {
                const ct = done.filter((p) => p.department === d.id).length;
                return (
                  <FilterItem
                    key={d.id}
                    checked={filters.department === d.id}
                    onClick={() => { setFilters((f) => ({ ...f, department: d.id })); setMenuOpen(null); }}
                    disabled={ct === 0}
                  >
                    <span className="pm-pri-dot" style={{ background: d.swatch }} />
                    <span>{d.label}</span>
                    <span className="ct">{ct}</span>
                  </FilterItem>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="pm-bl-rail-filter-wrap">
          <button
            type="button"
            className={`pm-bl-rail-filter-btn${filters.priority !== 'all' ? ' is-on' : ''}`}
            onClick={() => setMenuOpen((v) => (v === 'priority' ? null : 'priority'))}
          >
            <span>{priLabel()}</span>
            <IconChevDown size={11} />
          </button>
          {menuOpen === 'priority' ? (
            <div className="pm-bl-rail-menu">
              <FilterItem
                checked={filters.priority === 'all'}
                onClick={() => { setFilters((f) => ({ ...f, priority: 'all' })); setMenuOpen(null); }}
              >
                <span>All priorities</span>
                <span className="ct">{done.length}</span>
              </FilterItem>
              <div className="pm-bl-rail-menu-sep" />
              {PROJECT_PRIORITY_ORDER.map((p) => {
                const ct = done.filter((b) => b.priority === p).length;
                return (
                  <FilterItem
                    key={p}
                    checked={filters.priority === p}
                    onClick={() => { setFilters((f) => ({ ...f, priority: p })); setMenuOpen(null); }}
                    disabled={ct === 0}
                  >
                    <span className="pm-pri-dot" style={{ background: PROJECT_PRIORITY_COLORS[p] }} />
                    <span>{PROJECT_PRIORITY_LABELS[p]}</span>
                    <span className="ct">{ct}</span>
                  </FilterItem>
                );
              })}
            </div>
          ) : null}
        </div>

        {activeFilterCount > 0 ? (
          <button
            type="button"
            className="pm-bl-rail-filter-clear"
            onClick={() => setFilters({ department: 'all', priority: 'all' })}
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="pm-bl-rail-body">
        {groups.length === 0 && unprioritizedItems.length === 0 ? (
          <div className="pm-bl-rail-empty">
            <div className="pm-bl-rail-empty-hd">
              {done.length === 0 ? 'Nothing done yet' : 'No matches'}
            </div>
            <div className="pm-bl-rail-empty-sub">
              {done.length === 0
                ? 'Set a project’s priority to "Done" to file it here.'
                : 'Try clearing your filters.'}
            </div>
          </div>
        ) : null}

        {groups.map((g) => (
          <div key={g.id} className="pm-bl-rail-section">
            <div className="pm-bl-rail-sec-hd">
              <span className="pm-pri-dot" style={{ background: g.color }} />
              <span>{g.label}</span>
              <span className="pm-bl-rail-sec-ct">{g.items.length}</span>
            </div>
            {g.items.map((p) => (
              <DoneRow
                key={p.id}
                project={p}
                taskCount={taskCounts[p.id] || 0}
                onOpen={() => history.push(`/Projects/${p.id}`)}
                onReopen={() => reopen(p.id)}
                onDragStart={onProjectDragStart}
                onDragEnd={onProjectDragEnd}
                getDept={getDept}
              />
            ))}
          </div>
        ))}

        {unprioritizedItems.length > 0 ? (
          <div className="pm-bl-rail-section">
            <div className="pm-bl-rail-sec-hd">
              <span className="pm-pri-dot" style={{ background: 'var(--rr-stone-300)' }} />
              <span>Unprioritized</span>
              <span className="pm-bl-rail-sec-ct">{unprioritizedItems.length}</span>
            </div>
            {unprioritizedItems.map((p) => (
              <DoneRow
                key={p.id}
                project={p}
                taskCount={taskCounts[p.id] || 0}
                onOpen={() => history.push(`/Projects/${p.id}`)}
                onReopen={() => reopen(p.id)}
                onDragStart={onProjectDragStart}
                onDragEnd={onProjectDragEnd}
                getDept={getDept}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="pm-bl-rail-foot">
        <span className="pm-bl-rail-foot-hint">
          Drag a card to the Done tab, or set its priority to "Done".
        </span>
      </div>
    </aside>
    </div>
    </div>,
    document.body,
  );
};

interface RowProps {
  project: Project;
  taskCount: number;
  onOpen: () => void;
  onReopen: () => void;
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  getDept: (id: string | undefined) => { label: string; swatch: string } | undefined;
}

const DoneRow: React.FC<RowProps> = ({
  project,
  taskCount,
  onOpen,
  onReopen,
  onDragStart,
  onDragEnd,
  getDept,
}) => {
  const dept = getDept(project.department);
  return (
    <div
      className="pm-bl-rail-item"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      draggable={!!onDragStart}
      onDragStart={(e) => {
        if (!onDragStart) return;
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', project.id);
        }
        onDragStart(project.id);
      }}
      onDragEnd={onDragEnd}
    >
      <div className="pm-bl-rail-item-main">
        <div className="pm-bl-rail-item-name">{project.title}</div>
        <div className="pm-bl-rail-item-meta">
          {dept ? (
            <>
              <span className="pm-pri-dot" style={{ background: dept.swatch, width: 6, height: 6 }} />
              <span>{dept.label}</span>
              <span aria-hidden>·</span>
            </>
          ) : null}
          <span>{taskCount} task{taskCount === 1 ? '' : 's'}</span>
        </div>
      </div>
      <button
        type="button"
        className="pm-bl-rail-promote"
        onClick={(e) => { e.stopPropagation(); onReopen(); }}
        title="Reopen — move out of Done"
      >
        Reopen
      </button>
    </div>
  );
};

interface FilterItemProps {
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

const FilterItem: React.FC<FilterItemProps> = ({ checked, onClick, disabled, children }) => {
  return (
    <button
      type="button"
      className={`pm-bl-rail-menu-item${checked ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <span className="pm-bl-rail-menu-check">
        {checked ? (
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 8.5 6.5 12 13 4.5" />
          </svg>
        ) : null}
      </span>
      <span className="pm-bl-rail-menu-label">{children}</span>
    </button>
  );
};

export default DoneRail;
